import { motion } from 'framer-motion';
import PageLayout from '../components/PageLayout';
import { Home, Wrench, MessageSquareMore } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

const Services = () => {
  const services = [
    {
      icon: <Home className="w-8 h-8 text-primary-600" />,
      title: "Custom Home Building",
      image: "./custom-home-construction-kannur.jpg",
      alt: "Custom home construction in Kannur by Master Home",
      description: "Create your dream home from the ground up with our expert builders.",
      features: [
        "Personalized design consultation",
        "Quality materials and craftsmanship",
        "Project management excellence",
        "Timely completion guarantee"
      ]
    },
    {
      icon: <Wrench className="w-8 h-8 text-primary-600" />,
      title: "Renovations",
      image: "./house-renovation-iritty.jpg",
      alt: "House renovation and remodeling in Iritty",
      description: "Transform your existing space into something extraordinary.",
      features: [
        "Kitchen and bathroom remodeling",
        "Room additions and extensions",
        "Interior and exterior upgrades",
        "Energy efficiency improvements"
      ]
    },
    {
      icon: <MessageSquareMore className="w-8 h-8 text-primary-600" />,
      title: "Consulting",
      image: "./villa-builders-taliparamba.jpg",
      alt: "Villa builders and construction consulting in Taliparamba",
      description: "Expert advice for your construction and renovation projects.",
      features: [
        "Professional project planning",
        "Budget optimization",
        "Material selection guidance",
        "Regulatory compliance support"
      ]
    }
  ];

  return (
    <PageLayout
      title="Our Services"
      subtitle="Comprehensive solutions for your home building and renovation needs"
    >
      <Helmet>
        <title>Our Construction Services in Kannur | Master Home</title>
        <meta name="description" content="Master Home offers premium custom home building, remodeling, and renovation services in Kannur, Kerala. Explore our comprehensive construction solutions." />
        <link rel="canonical" href="https://masterhome.co.in/services" />
      </Helmet>
      <div className="space-y-16">
        {services.map((service, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.2 }}
            className="glass-card p-8 md:p-12"
          >
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
              <div className="space-y-6">
                <div className="w-16 h-16 bg-primary-50 rounded-xl flex items-center justify-center">
                  {service.icon}
                </div>
                <h2 className="text-3xl font-bold text-gradient">{service.title}</h2>
                <p className="text-gray-600 text-lg">{service.description}</p>
                <ul className="space-y-4">
                  {service.features.map((feature, featureIndex) => (
                    <motion.li
                      key={featureIndex}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.2 + featureIndex * 0.1 }}
                      className="flex items-center space-x-3"
                    >
                      <div className="w-2 h-2 bg-primary-500 rounded-full" />
                      <span className="text-gray-700">{feature}</span>
                    </motion.li>
                  ))}
                </ul>
              </div>
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-r from-primary-500 to-primary-600 rounded-2xl transform rotate-2 opacity-10"></div>
                <img
                  src={service.image}
                  alt={service.alt}
                  className="relative rounded-2xl shadow-xl w-full object-cover aspect-[4/3]"
                />
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      <div className="mt-16 text-center max-w-3xl mx-auto p-6 bg-gray-50 rounded-2xl">
        <h3 className="text-xl font-bold mb-3">Serving Our Local Communities</h3>
        <p className="text-gray-600">
          Looking for trusted <a href="/locations/kannur" className="text-primary-600 font-semibold hover:underline">Home builders in Kannur</a>? We also offer top-tier <a href="/locations/iritty" className="text-primary-600 font-semibold hover:underline">Construction services in Iritty</a> and premium villa development in <a href="/locations/taliparamba" className="text-primary-600 font-semibold hover:underline">Taliparamba</a>.
        </p>
      </div>
    </PageLayout>
  );
};

export default Services;
